import { ResponseError } from '@backstage/errors';
import {
  ArgoWorkflowsApi,
  ListWorkflowsOptions,
  SubmitWorkflowOptions,
} from './ArgoWorkflowsApi';
import {
  ArtifactBlob,
  Workflow,
  WorkflowNode,
  WorkflowPhase,
  WorkflowSummary,
  WorkflowTemplate,
} from './types';

interface FetchApi {
  fetch: (input: RequestInfo, init?: RequestInit) => Promise<Response>;
}

interface DiscoveryApi {
  getBaseUrl(pluginId: string): Promise<string>;
}

const NAMESPACE_CACHE_TTL = 5 * 60 * 1000;
const SEED_NAMESPACES = ['ci', 'terraform', 'ci-uks', 'qa', 'argo'];

export class ArgoWorkflowsClient implements ArgoWorkflowsApi {
  private readonly fetchApi: FetchApi;
  private readonly discoveryApi: DiscoveryApi;
  private nsCache: { promise: Promise<string[]>; ts: number } | null = null;

  constructor(deps: { fetchApi: FetchApi; discoveryApi: DiscoveryApi }) {
    this.fetchApi = deps.fetchApi;
    this.discoveryApi = deps.discoveryApi;
  }

  private async base(): Promise<string> {
    const proxy = await this.discoveryApi.getBaseUrl('proxy');
    return `${proxy}/argo-workflows`;
  }

  async discoverNamespaces(): Promise<string[]> {
    if (this.nsCache && Date.now() - this.nsCache.ts < NAMESPACE_CACHE_TTL) {
      return this.nsCache.promise;
    }
    const promise = this.doDiscoverNamespaces();
    this.nsCache = { promise, ts: Date.now() };
    promise.catch(() => { this.nsCache = null; });
    return promise;
  }

  private async doDiscoverNamespaces(): Promise<string[]> {
    const base = await this.base();
    try {
      const res = await this.fetchApi.fetch(`${base}/api/v1/workflow-templates/`);
      if (res.ok) {
        const json = (await res.json()) as { items?: { metadata: { namespace: string } }[] };
        const namespaces = [...new Set((json.items ?? []).map(t => t.metadata.namespace))];
        return namespaces.filter(Boolean).sort();
      }
    } catch { /* fall through to seed probe */ }

    const results = await Promise.all(
      SEED_NAMESPACES.map(async ns => {
        try {
          const res = await this.fetchApi.fetch(
            `${base}/api/v1/workflow-templates/${encodeURIComponent(ns)}`,
          );
          if (!res.ok) return null;
          const json = (await res.json()) as { items?: unknown[] };
          return (json.items?.length ?? 0) > 0 ? ns : null;
        } catch { return null; }
      }),
    );
    return results.filter((ns): ns is string => ns !== null).sort();
  }

  async listWorkflows(opts: ListWorkflowsOptions): Promise<WorkflowSummary[]> {
    const base = await this.base();
    const url = new URL(`${base}/api/v1/workflows/${encodeURIComponent(opts.namespace)}`);
    if (opts.labelSelector && opts.phases && opts.phases.length > 0) {
      url.searchParams.set(
        'listOptions.labelSelector',
        `${opts.labelSelector},workflows.argoproj.io/phase in (${opts.phases.join(',')})`,
      );
    } else if (opts.labelSelector) {
      url.searchParams.set('listOptions.labelSelector', opts.labelSelector);
    } else if (opts.phases && opts.phases.length > 0) {
      url.searchParams.set(
        'listOptions.labelSelector',
        `workflows.argoproj.io/phase in (${opts.phases.join(',')})`,
      );
    }
    url.searchParams.set('listOptions.limit', String(opts.limit ?? 50));

    const res = await this.fetchApi.fetch(url.toString());
    if (!res.ok) throw await ResponseError.fromResponse(res);
    const json = (await res.json()) as { items?: ArgoWorkflowApiObject[] };
    return (json.items ?? []).map(toSummary);
  }

  async listWorkflowTemplates(namespace: string): Promise<WorkflowTemplate[]> {
    const base = await this.base();
    const url = `${base}/api/v1/workflow-templates/${encodeURIComponent(namespace)}`;
    const res = await this.fetchApi.fetch(url);
    if (!res.ok) throw await ResponseError.fromResponse(res);
    const json = (await res.json()) as { items?: ArgoWorkflowTemplateApiObject[] };
    return (json.items ?? []).map(t => ({
      namespace: t.metadata.namespace,
      name: t.metadata.name,
      parameters: t.spec?.arguments?.parameters?.map(p => ({
        name: p.name,
        value: p.default ?? p.value,
        description: p.description,
      })),
    }));
  }

  async submitWorkflow(opts: SubmitWorkflowOptions): Promise<{ namespace: string; name: string }> {
    const base = await this.base();
    const url = `${base}/api/v1/workflows/${encodeURIComponent(opts.namespace)}`;
    const body = {
      workflow: {
        metadata: {
          namespace: opts.namespace,
          generateName: `${opts.templateName}-`,
          labels: opts.labels,
        },
        spec: {
          workflowTemplateRef: { name: opts.templateName },
          arguments: opts.parameters
            ? { parameters: Object.entries(opts.parameters).map(([name, value]) => ({ name, value })) }
            : undefined,
        },
      },
    };
    const res = await this.fetchApi.fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw await ResponseError.fromResponse(res);
    const json = (await res.json()) as ArgoWorkflowApiObject;
    return { namespace: json.metadata.namespace, name: json.metadata.name };
  }

  async getWorkflow(namespace: string, name: string): Promise<Workflow> {
    const base = await this.base();
    const url = `${base}/api/v1/workflows/${encodeURIComponent(namespace)}/${encodeURIComponent(name)}`;
    const res = await this.fetchApi.fetch(url);
    if (res.ok) {
      const json = (await res.json()) as ArgoWorkflowApiObject;
      return toWorkflow(json);
    }
    if (res.status !== 404) throw await ResponseError.fromResponse(res);

    // Workflow GC'd — find UID from archive list, then fetch full detail
    const archiveUrl = new URL(`${base}/api/v1/archived-workflows`);
    archiveUrl.searchParams.set(
      'listOptions.fieldSelector',
      `metadata.namespace=${namespace},metadata.name=${name}`,
    );
    archiveUrl.searchParams.set('listOptions.limit', '1');
    const archiveRes = await this.fetchApi.fetch(archiveUrl.toString());
    if (!archiveRes.ok) throw await ResponseError.fromResponse(archiveRes);
    const archiveJson = (await archiveRes.json()) as { items?: ArgoWorkflowApiObject[] };
    const archiveSummary = archiveJson.items?.[0];
    if (!archiveSummary) throw await ResponseError.fromResponse(res);

    const uid = archiveSummary.metadata.uid;
    const detailRes = await this.fetchApi.fetch(
      `${base}/api/v1/archived-workflows/${encodeURIComponent(uid)}`,
    );
    if (!detailRes.ok) throw await ResponseError.fromResponse(detailRes);
    const archived = (await detailRes.json()) as ArgoWorkflowApiObject;
    return { ...toWorkflow(archived), isArchived: true };
  }

  async resumeWorkflow(namespace: string, name: string): Promise<void> {
    const base = await this.base();
    const url = `${base}/api/v1/workflows/${encodeURIComponent(namespace)}/${encodeURIComponent(name)}/resume`;
    const res = await this.fetchApi.fetch(url, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: '{}' });
    if (!res.ok) throw await ResponseError.fromResponse(res);
  }

  async stopWorkflow(namespace: string, name: string): Promise<void> {
    const base = await this.base();
    const url = `${base}/api/v1/workflows/${encodeURIComponent(namespace)}/${encodeURIComponent(name)}/stop`;
    const res = await this.fetchApi.fetch(url, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: '{}' });
    if (!res.ok) throw await ResponseError.fromResponse(res);
  }

  async getArtifact(
    namespace: string,
    workflowName: string,
    nodeId: string,
    artifactName: string,
    opts?: { archivedUid?: string },
  ): Promise<ArtifactBlob> {
    const base = await this.base();
    const url = opts?.archivedUid
      ? `${base}/artifact-files/${encodeURIComponent(namespace)}/archived-workflows/${encodeURIComponent(
          opts.archivedUid,
        )}/${encodeURIComponent(nodeId)}/outputs/${encodeURIComponent(artifactName)}`
      : `${base}/artifact-files/${encodeURIComponent(namespace)}/workflows/${encodeURIComponent(
          workflowName,
        )}/${encodeURIComponent(nodeId)}/outputs/${encodeURIComponent(artifactName)}`;
    const res = await this.fetchApi.fetch(url);
    if (!res.ok) throw await ResponseError.fromResponse(res);
    const cloneForText = res.clone();
    const cloneForBlob = res.clone();
    return {
      filename: artifactName,
      contentType: res.headers.get('content-type') ?? 'application/octet-stream',
      size: Number(res.headers.get('content-length') ?? 0),
      text: () => cloneForText.text(),
      blob: () => cloneForBlob.blob(),
    };
  }

  async getNodeLogs(
    namespace: string,
    workflowName: string,
    podName: string,
    container = 'main',
  ): Promise<string> {
    const base = await this.base();
    const url = new URL(
      `${base}/api/v1/workflows/${encodeURIComponent(namespace)}/${encodeURIComponent(workflowName)}/log`,
    );
    url.searchParams.set('podName', podName);
    url.searchParams.set('logOptions.container', container);
    const res = await this.fetchApi.fetch(url.toString());
    if (!res.ok) throw await ResponseError.fromResponse(res);
    // Argo streams NDJSON of {result: {content, podName}}; each content chunk
    // is one log line, sometimes with a trailing \n and sometimes without.
    // Strip trailing \n from each chunk and rejoin so PlainTextView gets clean lines.
    const raw = await res.text();
    return raw
      .split('\n')
      .filter(Boolean)
      .map(line => {
        try {
          const parsed = JSON.parse(line) as { result?: { content?: string } };
          return (parsed.result?.content ?? '').replace(/\n$/, '');
        } catch {
          return line;
        }
      })
      .join('\n');
  }
}

interface ArgoWorkflowApiObject {
  metadata: {
    namespace: string;
    name: string;
    uid: string;
    labels?: Record<string, string>;
  };
  spec?: {
    workflowTemplateRef?: { name?: string };
    templates?: { name: string }[];
    arguments?: {
      parameters?: { name: string; value?: string }[];
    };
  };
  status?: {
    phase?: WorkflowPhase;
    startedAt?: string;
    finishedAt?: string;
    nodes?: Record<string, ArgoNodeApiObject>;
  };
}

interface ArgoWorkflowTemplateApiObject {
  metadata: { namespace: string; name: string };
  spec?: {
    arguments?: {
      parameters?: { name: string; default?: string; value?: string; description?: string }[];
    };
  };
}

interface ArgoNodeApiObject {
  id: string;
  name: string;
  displayName: string;
  type: WorkflowNode['type'];
  phase: WorkflowPhase;
  startedAt?: string;
  finishedAt?: string;
  templateName?: string;
  templateRef?: { template?: string; name?: string; clusterScope?: boolean };
  children?: string[];
  outputs?: WorkflowNode['outputs'];
  message?: string;
  podName?: string;
}

// Argo Workflows v3.6+ stopped persisting `podName` on the node API object
// when POD_NAMES=v2. The pod name is derived as
//   <workflow>-<templateName||templateRef.template>-<nodeId-suffix>
// where the nodeId-suffix is the trailing hash from the node id.
//
// This matches Argo's getPodName(workflowName, nodeName, templateName, nodeId)
// from workflow/util/util.go for names short enough to skip the SHA1 fallback
// (the >253 byte case). All our templates are well under that limit.
function derivePodName(workflowName: string, node: ArgoNodeApiObject): string | undefined {
  if (node.type !== 'Pod') return undefined;
  if (node.podName) return node.podName;
  const tmpl = node.templateName || node.templateRef?.template;
  if (!tmpl) return undefined;
  // nodeId is `<workflow>-<suffix>`; the suffix is what shows up at the end
  // of the actual pod name. Argo uses the entire trailing segment after the
  // last dash, regardless of whether it's a hash or sequential.
  const suffix = node.id.split('-').pop();
  if (!suffix || suffix === node.id) return undefined;
  return `${workflowName}-${tmpl}-${suffix}`;
}

function toSummary(o: ArgoWorkflowApiObject): WorkflowSummary {
  return {
    namespace: o.metadata.namespace,
    name: o.metadata.name,
    uid: o.metadata.uid,
    phase: o.status?.phase ?? 'Unknown',
    startedAt: o.status?.startedAt,
    finishedAt: o.status?.finishedAt,
    templateName: o.spec?.workflowTemplateRef?.name,
    labels: o.metadata.labels ?? {},
    arguments: o.spec?.arguments?.parameters
      ?.filter((p): p is { name: string; value: string } => p.value != null)
      .map(p => ({ name: p.name, value: p.value })),
  };
}

function toWorkflow(o: ArgoWorkflowApiObject): Workflow {
  const nodes: Record<string, WorkflowNode> = {};
  const apiNodes = o.status?.nodes ?? {};
  for (const [id, n] of Object.entries(apiNodes)) {
    nodes[id] = {
      id: n.id,
      name: n.name,
      displayName: n.displayName,
      type: n.type,
      phase: n.phase,
      startedAt: n.startedAt,
      finishedAt: n.finishedAt,
      templateName: n.templateName,
      templateRefTemplate: n.templateRef?.template,
      podName: derivePodName(o.metadata.name, n),
      children: n.children ?? [],
      outputs: n.outputs,
      message: n.message,
    };
  }
  // Argo convention: root node id == workflow name
  const rootNodeId = o.metadata.name;
  return {
    namespace: o.metadata.namespace,
    name: o.metadata.name,
    uid: o.metadata.uid,
    phase: o.status?.phase ?? 'Unknown',
    startedAt: o.status?.startedAt,
    finishedAt: o.status?.finishedAt,
    templateName: o.spec?.workflowTemplateRef?.name,
    labels: o.metadata.labels ?? {},
    nodes,
    rootNodeId,
  };
}
